import { useNavigate } from 'react-router-dom';
import { ChevronLeft, CircleHelp, Stethoscope } from 'lucide-react';
import { useDoctorProfilesStore } from '@/store/doctorProfilesStore';
import { ProfileAvatar } from '@/components/ui/Profile';
import { useHaptic } from '@/app/hooks/useHaptic';
import { getBridge } from '@/platform';

export interface AppBarProps {
  subtitle: string;
  back?: boolean;
  backTo?: string;
  /** На экранах онбординга вместо переключателя профиля показывается помощь. */
  onboarding?: boolean;
}

/**
 * Шапка приложения.
 *
 * В Telegram и MAX системная кнопка «Назад» рисуется самим клиентом, но в
 * WebView она есть не всегда (и отсутствует в браузере), поэтому собственная
 * кнопка остаётся всегда — это единственный надёжный способ вернуться.
 */
export function AppBar({ subtitle, back, backTo, onboarding }: AppBarProps) {
  const navigate = useNavigate();
  const profiles = useDoctorProfilesStore((state) => state.profiles);
  const activeId = useDoctorProfilesStore((state) => state.activeId);
  const profile = profiles.find((item) => item.doctor_profile_id === activeId);
  const haptic = useHaptic();

  const goBack = () => {
    haptic.impact('light');
    if (backTo) navigate(backTo);
    else if (window.history.length > 1) navigate(-1);
    else navigate('/home');
  };

  return (
    <header
      className="sticky top-0 z-20 flex min-h-[52px] items-center gap-2.5 border-b border-line/70 bg-surface/95 px-3 py-2 backdrop-blur"
      style={{ paddingTop: 'calc(8px + var(--bd-safe-top))' }}
    >
      {back ? (
        <button
          type="button"
          onClick={goBack}
          aria-label="Назад"
          className="bd-focus flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>
      ) : (
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-brand text-black"
          aria-hidden
        >
          <Stethoscope className="h-[17px] w-[17px]" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <strong className="block truncate text-[14px] font-medium">Бренд врача</strong>
        <span className="block truncate text-[11px] text-muted">{subtitle}</span>
      </div>

      {onboarding || !profile ? (
        <button
          type="button"
          onClick={() => navigate('/help')}
          aria-label="Помощь"
          className="bd-focus flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted"
        >
          <CircleHelp className="h-5 w-5" aria-hidden />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => {
            haptic.impact('light');
            navigate('/profiles');
          }}
          aria-label={`Активный профиль: ${profile.profile_name}. Сменить профиль`}
          className="bd-focus shrink-0 rounded-full"
        >
          <ProfileAvatar short={shortCode(profile.profile_name)} size={30} />
        </button>
      )}
    </header>
  );
}

/** Двухбуквенный код профиля для аватара. Имя может не прийти с сервера. */
function shortCode(source: string | null | undefined): string {
  const name = source ?? '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return (name.trim().slice(0, 2) || 'ПР').toUpperCase();
}

/** Заголовок с именем пользователя для главной. */
export function useGreeting() {
  const user = getBridge().getUser();
  const hour = new Date().getHours();
  const part =
    hour < 5 ? 'Доброй ночи' : hour < 12 ? 'Доброе утро' : hour < 18 ? 'Добрый день' : 'Добрый вечер';
  return user?.firstName ? `${part}, ${user.firstName}` : part;
}
